import {
  ApostleFilterInput,
  ApostleLite,
  ApostleSortBy
} from '@apeiron/apostle'
import { SortInput } from '@apeiron/common'
import { ApeironBlueButton, MayNull, useMessageContext } from '@apeiron/temp'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Box,
  Dialog,
  IconButton,
  Paper,
  styled,
  Typography
} from '@mui/material'
import FetchedApostleListPersonal from '@src/components/apostles/list/FetchedApostleListPersonal'
import { RenderPopupCardProps } from '@src/components/apostles/share/multi-select/ApostleMultiSelectionSlots'
import ApeironSelectExceedMessage from '@src/components/share/apeiron/ApeironSelectExceedMessage'
import { DEFAULT_MESSAGE_SHOW_DURATION } from '@src/constants/layout'
import { GetMyApostlesRequestInput } from '@src/hooks/graphql/useGetMyApostles'
import * as R from 'ramda'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const StyledPaper = styled(Paper)`
  background-color: #171719;

  &.MuiDialog-paper {
    margin: 0px;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    height: 800px;
    overflow: hidden;

    ${props => props.theme.breakpoints.down('res768')} {
      width: 100%;
    }
  }

  ${props => props.theme.breakpoints.up('res768')} {
    width: 80vw;
    max-width: 1200px;
  }

  .List-Template-Main-Panel {
    ${props => props.theme.breakpoints.down('res1024')} {
      height: 80vh;
    }
  }
`

const ConfirmButton = styled(ApeironBlueButton)`
  margin: 10px 10px 10px auto;
  ${props => props.theme.breakpoints.up('res480')} {
    width: 200px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: center;
    width: 100%;
    max-width: 360px;
  }
`

const MultiSelectText = styled(Typography)`
  margin-left: auto;
  margin-right: 15px;
  color: #999999;
  strong {
    color: #f1b535;
  }
`

const ConfirmButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-left: auto;
`

const CloseButtonContainer = styled(Box)`
  padding: 5px 20px;
  display: flex;
  justify-content: flex-end;
`

const StyledIconButton = styled(IconButton)`
  color: #ffffff;
`

const ApostleSelectionPopup = ({
  defaultFilterInput,
  defaultSortInput,
  isOpen,
  selectedApostles,
  selectLimit,
  allowEmptySelect = false,
  onClose,
  onConfirm,
  overrideRequestParameters,
  renderCard
}: Props) => {
  const [selectingApostles, setSelectingApostles] = useState<ApostleLite[]>([])

  const { setInfo } = useMessageContext()

  const { t } = useTranslation()

  useEffect(() => {
    setSelectingApostles(selectedApostles)
  }, [selectedApostles])

  const handleClick = useCallback(
    (apostle: MayNull<ApostleLite>) => {
      if (!apostle) {
        return
      }

      const isSelected = selectingApostles.find(a => a.id === apostle.id)

      if (!isSelected && selectingApostles.length >= selectLimit) {
        setInfo({
          message: (
            <ApeironSelectExceedMessage
              message={t('common.select_exceed_limit', {
                count: selectLimit
              })}
            />
          ),
          skipTouch: true,
          timeout: DEFAULT_MESSAGE_SHOW_DURATION
        })

        return
      }

      if (selectLimit > 1) {
        setSelectingApostles(prev => {
          if (isSelected) {
            return prev.filter(a => a.id !== apostle.id)
          }

          if (prev.length >= selectLimit) {
            return prev
          }

          return [...prev, apostle]
        })
      } else if (selectLimit === 1) {
        if (isSelected) {
          setSelectingApostles([])
        } else {
          onConfirm([apostle])
          onClose()
        }
      }
    },
    [onClose, onConfirm, selectLimit, selectingApostles, setInfo, t]
  )

  const handleOnConfirm = useCallback(() => {
    onConfirm(selectingApostles)
    onClose()
  }, [onClose, onConfirm, selectingApostles])

  const handleClose = useCallback(() => {
    setSelectingApostles(selectedApostles)
    onClose()
  }, [onClose, selectedApostles])

  const handleRenderCard = useCallback(
    (apostle: ApostleLite) => {
      const isSelected = selectingApostles.some(a => a.id === apostle.id)

      return renderCard({
        apostle,
        isSelected,
        onClick: handleClick,
        selectingApostles
      })
    },
    [selectingApostles, renderCard, handleClick]
  )

  return (
    <Dialog open={isOpen} onClose={handleClose} PaperComponent={StyledPaper}>
      <CloseButtonContainer>
        <StyledIconButton onClick={handleClose}>
          <CloseRoundedIcon />
        </StyledIconButton>
      </CloseButtonContainer>
      <FetchedApostleListPersonal
        popupMode
        renderCard={handleRenderCard}
        defaultFilterInput={defaultFilterInput}
        defaultSortInput={defaultSortInput}
        overrideRequestParameters={overrideRequestParameters}
        bottomBar={
          selectLimit > 0 && (
            <ConfirmButtonContainer>
              {selectLimit > 1 && (
                <ConfirmButton
                  disabled={R.isEmpty(selectingApostles) && !allowEmptySelect}
                  onClick={handleOnConfirm}
                >
                  {t('common.button.ok')}
                  {!R.isEmpty(selectingApostles) &&
                    ` (${R.length(selectingApostles)})`}
                </ConfirmButton>
              )}
              <MultiSelectText>
                <Trans>
                  {t('pagination.multi_select_limit', {
                    count: selectLimit
                  })}
                </Trans>
              </MultiSelectText>
            </ConfirmButtonContainer>
          )
        }
      />
    </Dialog>
  )
}

type Props = {
  defaultFilterInput?: ApostleFilterInput
  defaultSortInput?: SortInput<ApostleSortBy>
  isOpen: boolean
  selectedApostles: ApostleLite[]
  selectLimit: number
  allowEmptySelect?: boolean
  onConfirm: (apostles: ApostleLite[]) => void
  onClose: () => void
  renderCard: (params: RenderPopupCardProps) => ReactNode
  overrideRequestParameters?: (
    requestInput: GetMyApostlesRequestInput
  ) => GetMyApostlesRequestInput
}

export default ApostleSelectionPopup
